/**
 * Centralized Authentication Service
 *
 * Consolidates ALL authentication API calls:
 * - Login
 * - Logout
 * - OTP Request/Verification
 * - Password Reset
 * - Agreement Sign
 * - First Login Password Change
 * - Token Refresh
 *
 * Uses:
 * - publicInstance for public endpoints (login, OTP, password reset, agreement, password change)
 * - api instance for authenticated endpoints (logout, refresh, change password for authorized users)
 */

import { AxiosResponse } from 'axios';
import publicInstance from '../public-api';
import api from '../api';
import {
  ILoginRequest,
  ILoginResponse,
  IRequestOtpEmailRequestBody,
  IRequestOtpEmailResponseBody,
  IVerifyOtpRequestBody,
  IVerifyTwoFactorRequest,
  IVerifyOtpResponseBody,
  IResetPasswordRequestBody,
  IResetPasswordResponseBody,
  ISubmitAgreementSignRequest,
  ISubmitAgreementSignResponse,
  IChangePasswordFirstLoginRequest,
  IChangePasswordFirstLoginResponse,
  IRequestMeVerificationRequestBody,
  IRequestMeVerificationResponseBody,
  ISelectOrganizationRequest,
  ISelectOrganizationByStudentRequest,
  IGuardianStudentsResponse,
} from '../../interface/auth.interface';
import { IErrorResponse } from '../../interface/main.interface';

/**
 * ============================================================================
 * LOGIN ENDPOINTS (Public - no auth required)
 * ============================================================================
 */

/**
 * Login endpoint
 *
 * Returns:
 * - Response with profile data (user, profile info, etc.)
 * - Tokens in response headers (accessToken, refreshToken, x-change-password-token)
 *
 * NOTE: Tokens stored in response headers are extracted and stored by reducer via tokenManager
 * (NOT in this service layer - following architectural pattern)
 */
export const login = async (data: ILoginRequest): Promise<AxiosResponse<ILoginResponse>> => {
  return publicInstance.post<ILoginResponse>('/auth/login', data, {
    withCredentials: true,
  });
};

/**
 * ============================================================================
 * OTP REQUEST ENDPOINTS (Public - no auth required)
 * ============================================================================
 */

/**
 * Request OTP via email
 *
 * Returns publicToken to use for next OTP verification step
 */
export const requestOtpEmail = async (
  data: IRequestOtpEmailRequestBody,
): Promise<AxiosResponse<IRequestOtpEmailResponseBody>> => {
  return publicInstance.post<IRequestOtpEmailResponseBody>('/auth/request-otp', data);
};

/**
 * Request user verification (send verification code)
 */
export const requestUserVerification = async (
  data: IRequestMeVerificationRequestBody,
): Promise<AxiosResponse<IRequestMeVerificationResponseBody>> => {
  return publicInstance.post<IRequestMeVerificationResponseBody>('/auth/verification', data);
};

/**
 * ============================================================================
 * OTP VERIFICATION ENDPOINTS (Public - requires publicToken from previous step)
 * ============================================================================
 */

/**
 * Verify OTP
 *
 * Returns publicToken to proceed with password reset
 */
export const verifyOtp = async (
  data: IVerifyOtpRequestBody,
): Promise<AxiosResponse<IVerifyOtpResponseBody>> => {
  return publicInstance.post<IVerifyOtpResponseBody>('/auth/verify-otp', data);
};

/**
 * ============================================================================
 * TWO-FACTOR AUTHENTICATION (CVA-3748) — mid-login, scoped 2FA token
 * ============================================================================
 */

/**
 * POST /auth/two-factor/verify — exchange the emailed code for real tokens.
 *
 * Returns the SAME shapes login can: accessToken/refreshToken in headers for a
 * single-org user, or a tempToken plus ORGANIZATION_SELECTION_REQUIRED for a
 * multi-org user. Device fields travel with the code because verification is what
 * actually creates the session.
 */
export const verifyTwoFactor = async (
  data: IVerifyTwoFactorRequest,
  twoFaToken: string,
): Promise<AxiosResponse<ILoginResponse>> => {
  return publicInstance.post<ILoginResponse>('/auth/two-factor/verify', data, {
    headers: { Authorization: `Bearer ${twoFaToken}` },
  });
};

/** POST /auth/two-factor/resend — issues a NEW code; the previous one stops working. */
export const resendTwoFactorCode = async (
  twoFaToken: string,
): Promise<AxiosResponse<{ data: { email: string; expiresInSeconds: number } }>> => {
  return publicInstance.post(
    '/auth/two-factor/resend',
    {},
    { headers: { Authorization: `Bearer ${twoFaToken}` } },
  );
};

/**
 * ============================================================================
 * PASSWORD RESET ENDPOINTS (Public - requires publicToken from OTP verification)
 * ============================================================================
 */

/**
 * Reset password with new password
 *
 * Requires publicToken from OTP verification step
 */
export const resetPassword = async (
  data: IResetPasswordRequestBody,
): Promise<AxiosResponse<IResetPasswordResponseBody>> => {
  return publicInstance.post<IResetPasswordResponseBody>('/auth/reset-password', data);
};

/**
 * ============================================================================
 * AGREEMENT SIGN ENDPOINTS (Public - requires publicToken from login/OTP)
 * ============================================================================
 */

/**
 * POST /auth/agreement/sign - Submit and sign agreement
 * Used in: Agreement sign flow (authentication)
 * Request: ISubmitAgreementSignRequest
 * Response: ISubmitAgreementSignResponse
 */
export const submitAgreementSign = async (
  data: ISubmitAgreementSignRequest,
): Promise<AxiosResponse<ISubmitAgreementSignResponse>> => {
  return publicInstance.post<ISubmitAgreementSignResponse>('/auth/agreement/sign', data);
};

/**
 * ============================================================================
 * FIRST LOGIN PASSWORD CHANGE (Public - requires x-change-password-token as publicToken)
 * ============================================================================
 */

/**
 * Change password on first login
 *
 * Requires publicToken (x-change-password-token from login response)
 * This is a temporary token valid only for first login password change
 */
export const changePasswordFirstLogin = async (
  data: IChangePasswordFirstLoginRequest,
): Promise<AxiosResponse<IChangePasswordFirstLoginResponse>> => {
  return publicInstance.post<IChangePasswordFirstLoginResponse>('/auth/change-password', data);
};

/**
 * ============================================================================
 * ORGANIZATION / STUDENT SELECTION (multi-org users & guardians)
 * ============================================================================
 */

/**
 * POST /auth/selectOrganization — exchange the short-lived temp token issued
 * by /auth/login (ORGANIZATION_SELECTION_REQUIRED) for real org-scoped tokens.
 *
 * New accessToken/refreshToken are returned in response HEADERS; the body is
 * the full auth response (same shape as a complete login).
 *
 * @param data - The selected organization id
 * @param token - The temp token from the login response `temptoken` header
 */
export const selectOrganization = async (
  data: ISelectOrganizationRequest,
  token: string,
): Promise<AxiosResponse<ILoginResponse>> => {
  return publicInstance.post<ILoginResponse>('/auth/selectOrganization', data, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

/**
 * POST /auth/selectOrganizationByStudent — guardian picks a student; backend
 * issues a NEW accessToken/refreshToken pair (headers) scoped to the student's
 * organization plus a full auth response body. Uses the current access token.
 */
export const selectOrganizationByStudent = async (
  data: ISelectOrganizationByStudentRequest,
): Promise<AxiosResponse<ILoginResponse>> => {
  return api.post<ILoginResponse>('/auth/selectOrganizationByStudent', data);
};

/**
 * GET /me/students — the authenticated guardian's students as a flat list, each
 * student carrying its organization (the frontend groups by organization for
 * the sidebar selector).
 */
export const getGuardianStudents = async (): Promise<AxiosResponse<IGuardianStudentsResponse>> => {
  return api.get<IGuardianStudentsResponse>('/me/students');
};

/**
 * ============================================================================
 * EMAIL CTA TOKEN EXCHANGE (Public - no auth required)
 * ============================================================================
 */

/**
 * Exchange a short-lived email CTA token (eat) for a session JWT.
 * Called when user clicks a billing email button that contains ?eat=TOKEN.
 * Returns accessToken, refreshToken, and the redirect path.
 */
export const exchangeEmailCtaToken = async (
  token: string,
): Promise<AxiosResponse<{ accessToken: string; refreshToken: string; redirect: string }>> => {
  return publicInstance.post('/auth/email-link', { token });
};

/**
 * ============================================================================
 * LOGOUT ENDPOINTS (Authenticated - requires accessToken)
 * ============================================================================
 */

/**
 * Logout endpoint
 *
 * Requires valid accessToken
 * Clears session and tokens server-side
 */
export const userLogout = async (): Promise<AxiosResponse<IErrorResponse>> => {
  return api.post<IErrorResponse>('/auth/logout');
};

/**
 * ============================================================================
 * TOKEN REFRESH ENDPOINTS (Authenticated - requires refreshToken)
 * ============================================================================
 */

/**
 * Refresh access token using refresh token
 *
 * Requires valid refreshToken
 * Returns new accessToken and refreshToken
 */
export const refreshToken = async (
  refreshToken: string,
): Promise<AxiosResponse<{ accesstoken: string; refreshtoken: string }>> => {
  return api.post(
    '/auth/refresh',
    {},
    {
      headers: { Authorization: `Bearer ${refreshToken}` },
    },
  );
};

/**
 * ============================================================================
 * SERVICE EXPORTS
 * ============================================================================
 */

export const authService = {
  // Login
  login,

  // OTP
  requestOtpEmail,
  requestUserVerification,
  verifyOtp,

  // Two-factor authentication (CVA-3748)
  verifyTwoFactor,
  resendTwoFactorCode,

  // Password Reset
  resetPassword,

  // Agreement
  submitAgreementSign,

  // First Login Password Change
  changePasswordFirstLogin,

  // Organization / student selection
  selectOrganization,
  selectOrganizationByStudent,
  getGuardianStudents,

  // Logout
  userLogout,

  // Token Refresh
  refreshToken,

  // Email CTA token exchange
  exchangeEmailCtaToken,
};

export default authService;
